var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { usersApi } from '@data/rest/api';
import { equalComparison } from '@data/timeline/comparisons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ColorPalette, modelDiffVersionsFormat, NumberFormatType, Period } from '@util/enums';
import { queryClient } from '../../App';
import { useAuth } from '../../providers/AuthProvider';
var USER_SETTINGS_VERSION = '3';
var defaultUserSettings = {
    version: USER_SETTINGS_VERSION,
    // user locale and language settings
    user_currency_locale_locale: 'de-DE',
    user_color_palette: ColorPalette.STANDARD,
    user_app_language: 'en-US',
    user_date_locale: 'de',
    // sidebars
    show_tags_sidebar: false,
    show_tasks_sidebar: false,
    // tag editor settings
    tag_description_column_visible: true,
    // qa settings
    qa_filtered_years: [],
    qa_interval_start: null,
    qa_interval_end: null,
    qa_period: Period.MONTH,
    // model table settings
    expanded_clusters: [],
    finder_expanded: true,
    number_format_type: NumberFormatType.FORMATTED,
    model_table_row_header_width: 150,
    model_table_interval_start: null,
    model_table_interval_end: null,
    model_table_show_sum: false,
    model_table_filtered_years: [],
    model_table_periods: [Period.MONTH],
    model_table_filtered_versions: [1],
    model_table_stacked: false,
    model_table_columns: [],
    model_table_sort: null,
    model_table_timeline_versions_by_year: {},
    model_table_timeline_comparisons: [],
    model_table_timeline_diffformat: [modelDiffVersionsFormat.ABSOLUTE],
    list_items_show: false,
    list_items_dynamic_fields: [],
    expression_show: false,
    backprop_cluster_path: null,
    backprop_forecast_version_id: null,
    backprop_destination_version_id: null,
};
export function userSettingsSelectFunction(userSettings) {
    if (!userSettings || userSettings.version !== USER_SETTINGS_VERSION) {
        return JSON.parse(JSON.stringify(defaultUserSettings));
    }
    return Object.assign(JSON.parse(JSON.stringify(defaultUserSettings)), userSettings);
}
/**
 * Get the user settings outside react
 * @returns user settings
 */
export function getUserSettings() {
    var userSettings = queryClient.getQueryData(['currentUserSettings']);
    if (!userSettings) {
        return userSettingsSelectFunction();
    }
    return userSettingsSelectFunction(userSettings);
}
export function useUpdateUserSettingsActions() {
    var _this = this;
    var queryClient = useQueryClient();
    var oldUserSettings = useAuth().settings;
    var mutation = useMutation(function (newUserSettings) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, usersApi.updateSmartSettings(newUserSettings)];
            case 1: return [2 /*return*/, _a.sent()];
        }
    }); }); }, {
        onMutate: function (newUserSettings) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, queryClient.cancelQueries(['currentUserSettings'])];
                    case 1:
                        _a.sent();
                        queryClient.setQueryData(['currentUserSettings'], newUserSettings);
                        return [2 /*return*/];
                }
            });
        }); },
        onSettled: function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                queryClient.invalidateQueries(['currentUserSettings']);
                return [2 /*return*/];
            });
        }); },
    });
    return {
        toggleDynamicField: function (key) {
            if (oldUserSettings.list_items_dynamic_fields.includes(key)) {
                oldUserSettings.list_items_dynamic_fields = oldUserSettings.list_items_dynamic_fields.filter(function (k) { return k !== key; });
            }
            else {
                oldUserSettings.list_items_dynamic_fields = __spreadArray(__spreadArray([], __read(oldUserSettings.list_items_dynamic_fields), false), [key], false);
            }
            mutation.mutate(oldUserSettings);
        },
        toggleListItemsShow: function () {
            oldUserSettings.list_items_show = !oldUserSettings.list_items_show;
            mutation.mutate(oldUserSettings);
        },
        toggleExpressionShow: function () {
            oldUserSettings.expression_show = !oldUserSettings.expression_show;
            mutation.mutate(oldUserSettings);
        },
        toggleVersion: function (versionId) {
            var idx = oldUserSettings.model_table_filtered_versions.indexOf(versionId);
            if (idx !== -1) {
                oldUserSettings.model_table_filtered_versions = oldUserSettings.model_table_filtered_versions.filter(function (vid) { return vid !== versionId; });
            }
            else {
                oldUserSettings.model_table_filtered_versions = __spreadArray(__spreadArray([], __read(oldUserSettings.model_table_filtered_versions), false), [
                    versionId,
                ], false);
            }
            mutation.mutate(oldUserSettings);
        },
        setNumberFormatType: function (numberFormatType) {
            oldUserSettings.number_format_type = numberFormatType;
            mutation.mutate(oldUserSettings);
        },
        expandCollapseCluster: function (clusterPath) {
            var idx = oldUserSettings.expanded_clusters.indexOf(clusterPath);
            if (idx !== -1) {
                oldUserSettings.expanded_clusters = oldUserSettings.expanded_clusters.filter(function (cls) { return cls !== clusterPath; });
            }
            else {
                oldUserSettings.expanded_clusters = __spreadArray(__spreadArray([], __read(oldUserSettings.expanded_clusters), false), [clusterPath], false);
            }
            mutation.mutate(oldUserSettings);
        },
        expandCollapseClusters: function (clusterPaths) {
            oldUserSettings.expanded_clusters = clusterPaths;
            mutation.mutate(oldUserSettings);
        },
        setModelTableIntervalStart: function (modelTableIntervalStart) {
            oldUserSettings.model_table_interval_start = modelTableIntervalStart;
            mutation.mutate(oldUserSettings);
        },
        setModelTableIntervalEnd: function (modelTableIntervalEnd) {
            oldUserSettings.model_table_interval_end = modelTableIntervalEnd;
            mutation.mutate(oldUserSettings);
        },
        addModelTableYear: function (year) {
            var newYears = new Set(oldUserSettings.model_table_filtered_years);
            newYears.add(year);
            oldUserSettings.model_table_filtered_years = Array.from(newYears);
            mutation.mutate(oldUserSettings);
        },
        removeModelTableYear: function (year) {
            oldUserSettings.model_table_filtered_years = oldUserSettings.model_table_filtered_years.filter(function (y) { return y !== year; });
            if (oldUserSettings.model_table_timeline_versions_by_year[year]) {
                // clean custom timeline
                delete oldUserSettings.model_table_timeline_versions_by_year[year];
                oldUserSettings.model_table_timeline_comparisons =
                    oldUserSettings.model_table_timeline_comparisons.filter(function (c) { return c.optionA.year !== year && c.optionB.year !== year; });
            }
            mutation.mutate(oldUserSettings);
        },
        setModelTableYears: function (years) {
            oldUserSettings.model_table_filtered_years = years;
            mutation.mutate(oldUserSettings);
        },
        setModelTablePeriod: function (period) {
            var arr = __spreadArray([], __read(oldUserSettings.model_table_periods), false) || [];
            if (arr.includes(period)) {
                arr = arr.filter(function (p) { return p !== period; });
            }
            else {
                arr = __spreadArray(__spreadArray([], __read(arr), false), [period], false);
            }
            oldUserSettings.model_table_periods = arr;
            mutation.mutate(oldUserSettings);
        },
        toggleModelTableShowSum: function () {
            oldUserSettings.model_table_show_sum = !oldUserSettings.model_table_show_sum;
            mutation.mutate(oldUserSettings);
        },
        setQaIntervalStart: function (qaIntervalStart) {
            oldUserSettings.qa_interval_start = qaIntervalStart;
            mutation.mutate(oldUserSettings);
        },
        setQaIntervalEnd: function (qaIntervalEnd) {
            oldUserSettings.qa_interval_end = qaIntervalEnd;
            mutation.mutate(oldUserSettings);
        },
        addQaYear: function (year) {
            var newYears = new Set(oldUserSettings.qa_filtered_years);
            newYears.add(year);
            oldUserSettings.qa_filtered_years = Array.from(newYears);
            mutation.mutate(oldUserSettings);
        },
        removeQaYear: function (year) {
            oldUserSettings.qa_filtered_years = oldUserSettings.qa_filtered_years.filter(function (y) { return y !== year; });
            mutation.mutate(oldUserSettings);
        },
        setQaPeriod: function (period) {
            oldUserSettings.qa_period = period;
            mutation.mutate(oldUserSettings);
        },
        setLocalesAndPalette: function (updates) {
            if (updates.appLanguage !== undefined) {
                oldUserSettings.user_app_language = updates.appLanguage;
            }
            if (updates.currencyLocale !== undefined) {
                oldUserSettings.user_currency_locale_locale = updates.currencyLocale;
            }
            if (updates.dateLocale !== undefined) {
                oldUserSettings.user_date_locale = updates.dateLocale;
            }
            if (updates.colorPalette !== undefined) {
                oldUserSettings.user_color_palette = updates.colorPalette;
            }
            mutation.mutate(oldUserSettings);
        },
        setTagDescriptionColumnVisible: function (visible) {
            oldUserSettings.tag_description_column_visible = visible;
            mutation.mutate(oldUserSettings);
        },
        saveUserSettings: function (userSettings) {
            mutation.mutate(userSettings);
        },
        toggleFinderExpanded: function () {
            oldUserSettings.finder_expanded = !oldUserSettings.finder_expanded;
            mutation.mutate(oldUserSettings);
        },
        updateBackpropSettings: function (forecastVersionId, destinationVersionId, clusterPath) {
            oldUserSettings.backprop_cluster_path = clusterPath;
            oldUserSettings.backprop_destination_version_id = destinationVersionId;
            oldUserSettings.backprop_forecast_version_id = forecastVersionId;
            mutation.mutate(oldUserSettings);
        },
        toggleModelTimelineVersionByYear: function (year, versionId) {
            var _a;
            var versionByYearCopy = __assign({}, oldUserSettings.model_table_timeline_versions_by_year);
            var yearVersions = (_a = versionByYearCopy[year]) !== null && _a !== void 0 ? _a : [];
            var removeVersion = yearVersions.includes(versionId);
            var newYearVersions = removeVersion
                ? yearVersions.filter(function (vId) { return vId !== versionId; })
                : __spreadArray(__spreadArray([], __read(yearVersions), false), [versionId], false);
            if (newYearVersions.length === 0) {
                delete versionByYearCopy[year];
            }
            else {
                versionByYearCopy[year] = newYearVersions;
            }
            oldUserSettings.model_table_timeline_versions_by_year = versionByYearCopy;
            if (removeVersion) {
                oldUserSettings.model_table_timeline_comparisons =
                    oldUserSettings.model_table_timeline_comparisons.filter(function (c) { return c.optionA.versionId !== versionId && c.optionB.versionId !== versionId; });
            }
            mutation.mutate(oldUserSettings);
        },
        toggleModelTimelineComparison: function (comparison, add) {
            var _a;
            var arr = (_a = oldUserSettings.model_table_timeline_comparisons) !== null && _a !== void 0 ? _a : [];
            oldUserSettings.model_table_timeline_comparisons = add
                ? __spreadArray(__spreadArray([], __read(arr), false), [comparison], false) : arr.filter(function (c) { return !equalComparison(c, comparison); });
            mutation.mutate(oldUserSettings);
        },
        toggleModelTimelineComparisonReverse: function (comparison) {
            var _a;
            var arr = (_a = oldUserSettings.model_table_timeline_comparisons) !== null && _a !== void 0 ? _a : [];
            var idx = arr.findIndex(function (c) { return equalComparison(c, comparison); });
            if (idx > -1) {
                arr[idx] = __assign(__assign({}, comparison), { reverse: !comparison.reverse });
            }
            oldUserSettings.model_table_timeline_comparisons = arr;
            mutation.mutate(oldUserSettings);
        },
        toggleModelTimelineDiffFormat: function (diffFormat) {
            var _a;
            var arr = (_a = oldUserSettings.model_table_timeline_diffformat) !== null && _a !== void 0 ? _a : [];
            oldUserSettings.model_table_timeline_diffformat = arr.includes(diffFormat)
                ? arr.filter(function (diff) { return diff !== diffFormat; })
                : __spreadArray(__spreadArray([], __read(arr), false), [diffFormat], false);
            mutation.mutate(oldUserSettings);
        },
        resetModelTimeline: function () {
            oldUserSettings.model_table_timeline_versions_by_year = {};
            oldUserSettings.model_table_timeline_comparisons = [];
            mutation.mutate(oldUserSettings);
        },
        toggleModelStacked: function () {
            oldUserSettings.model_table_stacked = !oldUserSettings.model_table_stacked;
            mutation.mutate(oldUserSettings);
        },
        toggleModelTableColumn: function (col) {
            var columns = oldUserSettings.model_table_columns || [];
            if (columns.includes(col)) {
                columns = columns.filter(function (c) { return c !== col; });
            }
            else {
                columns = __spreadArray(__spreadArray([], __read(columns), false), [col], false);
            }
            oldUserSettings.model_table_columns = columns;
            mutation.mutate(oldUserSettings);
        },
        setModelTableSort: function (sort) {
            oldUserSettings.model_table_sort = sort;
            mutation.mutate(oldUserSettings);
        },
        toggleShowTagsSidebar: function (show) {
            oldUserSettings.show_tags_sidebar = show;
            if (show) {
                oldUserSettings.show_tasks_sidebar = false;
            }
            mutation.mutate(oldUserSettings);
        },
        toggleShowTasksSidebar: function (show) {
            oldUserSettings.show_tasks_sidebar = show;
            if (show) {
                oldUserSettings.show_tags_sidebar = false;
            }
            mutation.mutate(oldUserSettings);
        },
    };
}
